import { useLayoutEffect, useRef, type RefObject } from 'react';
import Link from '@/components/base/TransitionLink';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SectionLabel from '@/components/feature/SectionLabel';
import ProjectImage from '@/components/feature/ProjectImage';
import { projects } from '@/mocks/site';

gsap.registerPlugin(ScrollTrigger);

type P = (typeof projects)[number];

/**
 * Row-level mask reveal. Clip-path lives on the ProjectImage wrapper and the 16px drift on .img-frame,
 * so the existing CSS hover scale on the inner <img> is never touched.
 */
function useRowReveal(row: RefObject<HTMLDivElement | null>, stagger: number) {
  useLayoutEffect(() => {
    const el = row.current;
    if (!el) return undefined;
    const mm = gsap.matchMedia();
    mm.add('(prefers-reduced-motion: no-preference)', () => {
      const masks = gsap.utils.toArray<HTMLElement>(el.querySelectorAll('[data-sw-mask]'));
      const frames = masks.map((m) => m.querySelector('.img-frame')).filter(Boolean) as HTMLElement[];
      const tl = gsap.timeline({
        scrollTrigger: { trigger: el, start: 'top 85%', once: true },
        defaults: { duration: 0.95, ease: 'power3.out' },
      });
      tl.fromTo(masks, { clipPath: 'inset(100% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', stagger, clearProps: 'clipPath' }, 0)
        .fromTo(frames, { y: 16 }, { y: 0, stagger, clearProps: 'transform' }, 0);
      return undefined;
    });
    return () => mm.revert(); // also restores visibility if unmounted mid-animation
  }, [row, stagger]);
}

function Caption({ p, large }: { p: P; large?: boolean }) {
  return (
    <div className="mt-3 flex items-baseline gap-3 border-t border-background-300/70 pt-3">
      <span className="meta text-accent-600 w-7 shrink-0">{p.number}</span>
      <div className="min-w-0 flex-1">
        <h3 className={`font-heading font-medium leading-snug group-hover:text-accent-600 transition-colors ${large ? 'text-[15px] md:text-base' : 'text-sm'}`}>{p.title}</h3>
        <p className="text-xs text-foreground-600 mt-0.5">{p.finish}</p>
      </div>
      {large && (
        <span className="meta whitespace-nowrap hidden sm:inline group-hover:text-foreground-950 transition-colors">
          View <i className="ri-arrow-right-up-line arrow-nudge" />
        </span>
      )}
    </div>
  );
}

export default function SelectedProjects() {
  const featured = projects.slice(0, 2);
  const supporting = projects.slice(2, 6);
  const rowA = useRef<HTMLDivElement>(null);
  const rowB = useRef<HTMLDivElement>(null);
  useRowReveal(rowA, 0.1);
  useRowReveal(rowB, 0.1);
  return (
    <section className="tone-white py-20 md:py-28">
      <div className="shell">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-6 mb-12 md:mb-16 items-end">
          <div className="md:col-span-3"><SectionLabel index="02">Selected Work</SectionLabel></div>
          <h2 data-reveal="title" className="md:col-span-6 md:col-start-4 h-section">Walls we have renewed</h2>
          <Link to="/projects" className="md:col-span-3 md:justify-self-end meta text-foreground-950 border-b border-foreground-950 pb-0.5 self-start md:self-auto whitespace-nowrap cursor-pointer hover:text-accent-600 hover:border-accent-600 w-fit">
            View all projects
          </Link>
        </div>

        <div ref={rowA} className="grid grid-cols-1 md:grid-cols-2 gap-x-4 md:gap-x-6 gap-y-10">
          {featured.map((p) => (
            <Link key={p.slug} to={`/projects/${p.slug}`} className="group block cursor-pointer min-w-0">
              <ProjectImage slug={p.slug} src={p.cover} srcSm={p.coverSm} sizes="(min-width: 768px) 50vw, 100vw" alt={`${p.title} — ${p.finish}`} className="w-full aspect-[4/3]" />
              <Caption p={p} large />
            </Link>
          ))}
        </div>

        <div ref={rowB} className="mt-14 md:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-x-4 md:gap-x-6 gap-y-10">
          {supporting.map((p) => (
            <Link key={p.slug} to={`/projects/${p.slug}`} className="group block cursor-pointer min-w-0">
              <ProjectImage slug={p.slug} src={p.cover} srcSm={p.coverSm} sizes="(min-width: 768px) 50vw, 100vw" alt={`${p.title} — ${p.finish}`} className="w-full aspect-[4/3]" />
              <Caption p={p} />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}